import { useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

type Mode = 'new' | 'existing';

interface GroupSkusModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSkuIds: string[];
  onGrouped: () => void | Promise<void>;
}

export default function GroupSkusModal({
  isOpen,
  onClose,
  selectedSkuIds,
  onGrouped,
}: GroupSkusModalProps) {
  const [mode, setMode] = useState<Mode>('new');
  const [title, setTitle] = useState('');
  const [productType, setProductType] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [comboboxOpen, setComboboxOpen] = useState(false);

  const { data: productsData } = trpc.inventory.listProductsGrouped.useQuery(
    { productionType: 'POD', pageSize: 100 },
    { enabled: isOpen && mode === 'existing' },
  );

  const podProducts = productsData?.products ?? [];

  const mutation = trpc.inventory.groupSkusUnderProduct.useMutation({
    onSuccess: async (result) => {
      toast.success(`Grouped ${result.groupedCount} SKU${result.groupedCount !== 1 ? 's' : ''} into product`);
      resetForm();
      onClose();
      await onGrouped();
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to group SKUs');
    },
  });

  const resetForm = () => {
    setMode('new');
    setTitle('');
    setProductType('');
    setImageUrl('');
    setSelectedProductId(null);
    setComboboxOpen(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const canSubmit =
    selectedSkuIds.length > 0 &&
    !mutation.isPending &&
    (mode === 'new' ? title.trim().length > 0 : selectedProductId != null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    if (mode === 'new') {
      mutation.mutate({
        skuIds: selectedSkuIds,
        newProduct: {
          title: title.trim(),
          productType: productType.trim() || undefined,
          imageUrl: imageUrl.trim() || undefined,
        },
      });
    } else {
      mutation.mutate({
        skuIds: selectedSkuIds,
        productId: selectedProductId!,
      });
    }
  };

  const selectedProduct = podProducts.find((p) => p.id === selectedProductId);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Group into Product</DialogTitle>
            <DialogDescription>
              Assign {selectedSkuIds.length} selected SKU{selectedSkuIds.length !== 1 ? 's' : ''} to a product.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Mode toggle */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={mode === 'new' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('new')}
                className="flex-1"
              >
                New Product
              </Button>
              <Button
                type="button"
                variant={mode === 'existing' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('existing')}
                className="flex-1"
              >
                Existing Product
              </Button>
            </div>

            {mode === 'new' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="group-title">Product Title *</Label>
                  <Input
                    id="group-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Classic T-Shirt"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="group-type">Product Type</Label>
                  <Input
                    id="group-type"
                    value={productType}
                    onChange={(e) => setProductType(e.target.value)}
                    placeholder="e.g. T-Shirt, Hoodie"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="group-image">Image URL</Label>
                  <Input
                    id="group-image"
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label>Select Product</Label>
                <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={comboboxOpen}
                      className="w-full justify-between font-normal"
                    >
                      {selectedProduct
                        ? selectedProduct.title || 'Untitled Product'
                        : 'Search products...'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search products..." />
                      <CommandList>
                        <CommandEmpty>No products found.</CommandEmpty>
                        <CommandGroup>
                          {podProducts.map((product) => (
                            <CommandItem
                              key={product.id}
                              value={`${product.title ?? ''} ${product.productType ?? ''}`}
                              onSelect={() => {
                                setSelectedProductId(
                                  product.id === selectedProductId ? null : product.id,
                                );
                                setComboboxOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  selectedProductId === product.id ? 'opacity-100' : 'opacity-0',
                                )}
                              />
                              <div className="flex flex-col min-w-0">
                                <span className="truncate">
                                  {product.title || 'Untitled Product'}
                                </span>
                                {product.productType && (
                                  <span className="text-xs text-muted-foreground">
                                    {product.productType} &middot; {product.variantCount} variant{product.variantCount !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Grouping...
                </>
              ) : (
                `Group ${selectedSkuIds.length} SKU${selectedSkuIds.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
